import React from "react";
import { Box, Typography, Chip, alpha, useTheme, Paper } from "@mui/material";
import { Grass, Spa, LocalFireDepartment } from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";

const OrderItemCard = ({ item, formatCurrency, compact = false }) => {
  const theme = useTheme();

  const imageSize = compact ? 70 : 100;
  const padding = compact ? 1 : 1.5;

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        gap: compact ? 1 : 2,
        p: padding,
        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: compact ? 2 : 3,
        background: "white",
        transition: "all 0.3s ease",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* Image - Left Side */}
      <Box
        sx={{
          width: imageSize,
          height: imageSize,
          flexShrink: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: compact ? 1.5 : 2,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.MenuItem?.image ? (
          <ImageLoader
            imagePath={item.MenuItem.image}
            width="100%"
            height={imageSize}
            alt={item.MenuItem.name}
            borderRadius={compact ? 1.5 : 2}
          />
        ) : (
          <Typography
            variant={compact ? "h4" : "h3"}
            sx={{
              opacity: 0.2,
              fontWeight: 700,
            }}
          >
            {item.MenuItem?.name?.charAt(0) || "?"}
          </Typography>
        )}

        {/* Diet Tags */}
        {!compact && (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              display: "flex",
              gap: 0.3,
              flexDirection: "column",
            }}
          >
            {item.MenuItem?.isVegetarian && (
              <Chip
                icon={<Grass sx={{ fontSize: 12 }} />}
                label="Veg"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.main, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  height: 18,
                  "& .MuiChip-icon": {
                    color: "white",
                    ml: 0.5,
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            )}
            {item.MenuItem?.isVegan && (
              <Chip
                icon={<Spa sx={{ fontSize: 12 }} />}
                label="Vegan"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.success.dark, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  height: 18,
                  "& .MuiChip-icon": {
                    color: "white",
                    ml: 0.5,
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            )}
            {item.MenuItem?.isSpicy && (
              <Chip
                icon={<LocalFireDepartment sx={{ fontSize: 12 }} />}
                label="Spicy"
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.9),
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  height: 18,
                  "& .MuiChip-icon": {
                    color: "white",
                    ml: 0.5,
                  },
                  "& .MuiChip-label": {
                    px: 0.5,
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Details - Middle Section */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
      >
        <Typography
          variant={compact ? "body2" : "subtitle1"}
          sx={{
            fontWeight: 700,
            mb: 0.5,
            fontSize: compact ? "0.85rem" : "0.95rem",
            lineHeight: 1.3,
          }}
        >
          {item.MenuItem?.name || "Menu Item"}
        </Typography>

        {/* Description */}
        {!compact && item.MenuItem?.description && (
          <Typography
            variant="body2"
            sx={{
              color: alpha(theme.palette.text.secondary, 0.7),
              fontSize: "0.8rem",
              mb: 0.8,
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item.MenuItem.description}
          </Typography>
        )}

        {/* Variation */}
        {item.ItemVariation && (
          <Chip
            label={item.ItemVariation.name}
            size="small"
            sx={{
              height: compact ? 18 : 22,
              fontSize: compact ? "0.65rem" : "0.7rem",
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              width: "fit-content",
              mb: 0.5,
            }}
          />
        )}

        {/* Modifiers */}
        {item.OrderItemModifiers && item.OrderItemModifiers.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: "auto" }}>
            {item.OrderItemModifiers.map((modifier, modIndex) => (
              <Chip
                key={modIndex}
                label={modifier.ModifierOption?.name || "Extra"}
                size="small"
                sx={{
                  height: compact ? 16 : 20,
                  fontSize: compact ? "0.6rem" : "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  color: theme.palette.text.secondary,
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Quantity & Price - Right Side */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          minWidth: compact ? 60 : 100,
        }}
      >
        {/* Quantity Badge */}
        <Box
          sx={{
            minWidth: compact ? 40 : 48,
            height: compact ? 40 : 48,
            borderRadius: compact ? 1.5 : 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <Typography
            sx={{
              fontSize: compact ? "1.1rem" : "1.3rem",
              fontWeight: 800,
              color: theme.palette.primary.main,
              lineHeight: 1,
            }}
          >
            {item.quantity}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: compact ? "0.55rem" : "0.6rem",
              fontWeight: 600,
              color: alpha(theme.palette.primary.main, 0.7),
              textTransform: "uppercase",
              lineHeight: 1,
            }}
          >
            Qty
          </Typography>
        </Box>

        {/* Price Info */}
        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: compact ? "0.6rem" : "0.65rem",
              display: "block",
              textTransform: "uppercase",
            }}
          >
            Total
          </Typography>
          <Typography
            variant={compact ? "body2" : "h6"}
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
              fontSize: compact ? "0.95rem" : "1.1rem",
              lineHeight: 1.2,
            }}
          >
            {formatCurrency(item.totalPrice)}
          </Typography>
          {!compact && item.quantity > 1 && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.7),
                fontSize: "0.7rem",
                display: "block",
              }}
            >
              {formatCurrency(item.unitPrice)} each
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default OrderItemCard;
